import {Slide} from '@aboutyou/transitions';
import React from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Z_INDEXES} from '../../../constants/layers';
import {useTimedOut} from '../../../hooks/useManualTimeout/useManualTimeout';
import {
  SCROLL_TO_TOP_X_OFFSET_CSS_VARIABLE,
  SCROLL_TO_TOP_Y_OFFSET_CSS_VARIABLE,
} from '../../../hooks/useSetScrollToTopOffset/useSetScrollToTopOffset';
import {GenericStep} from './GenericStep';
import {SearchResultsHelpful} from './SearchResultsHelpful';
import {ThanksForYourFeeback} from './ThanksForYourFeedback';

export const SR_QUEST_TEST_ID = 'SearchResultQuestionnaire';

export type SearchResultQuestionnaireStep =
  | 'initial'
  | 'optionSelection'
  | 'feedbackInput'
  | 'thankYou'
  | 'closed';

export interface SearchResultQuestionnaireWidgetProps {
  step: SearchResultQuestionnaireStep;
  onClose: VoidFunction;
  onThumbsUpClick: VoidFunction;
  onThumbsDownClick: VoidFunction;
  onFirstOptionSelected: VoidFunction;
  onSecondOptionSelected: VoidFunction;
  onSaveAndCloseClick: (answer: string) => void;
}

export const SearchResultQuestionnaireWidget: React.FC<
  SearchResultQuestionnaireWidgetProps
> = ({
  step,
  onClose,
  onThumbsUpClick,
  onThumbsDownClick,
  onFirstOptionSelected,
  onSecondOptionSelected,
  onSaveAndCloseClick,
}) => {
  const delay = useTimedOut(1500);

  return (
    <>
      <Slide direction="up" mounted={delay} duration={300}>
        <Wrapper data-step={step} data-testid={SR_QUEST_TEST_ID}>
          {step === 'initial' && (
            <SearchResultsHelpful
              dataTestId={`${SR_QUEST_TEST_ID}-${step}`}
              onThumbsUpClick={onThumbsUpClick}
              onThumbsDownClick={onThumbsDownClick}
            />
          )}

          {step === 'optionSelection' && (
            <GenericStep
              dataTestId={`${SR_QUEST_TEST_ID}-${step}`}
              step={step}
              onFirstOptionSelected={onFirstOptionSelected}
              onSecondOptionSelected={onSecondOptionSelected}
              onClose={onClose}
            />
          )}

          {step === 'feedbackInput' && (
            <GenericStep
              dataTestId={`${SR_QUEST_TEST_ID}-${step}`}
              step={step}
              onFirstOptionSelected={onFirstOptionSelected}
              onSecondOptionSelected={onSecondOptionSelected}
              onSaveAndCloseClick={onSaveAndCloseClick}
              onClose={onClose}
            />
          )}
        </Wrapper>
      </Slide>

      <Slide direction="up" mounted={step === 'thankYou'} duration={300}>
        <ThanksForYourFeebackWrapper
          data-testid={`${SR_QUEST_TEST_ID}-${step}`}
        >
          <ThanksForYourFeeback />
        </ThanksForYourFeebackWrapper>
      </Slide>
    </>
  );
};

const widgetPosition = css`
  position: fixed;
  bottom: calc(16px + var(${SCROLL_TO_TOP_Y_OFFSET_CSS_VARIABLE}, 0px));
  right: var(${SCROLL_TO_TOP_X_OFFSET_CSS_VARIABLE}, 76px);
  z-index: ${Z_INDEXES.SEARCH_RESULTS_QUESTIONNAIRE};
`;

const Wrapper = styled.div`
  ${widgetPosition}
  color: ${COLORS['Text.Black']};
  background-color: ${COLORS['Fill.White']};
  border-radius: 25px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.24);
  width: fit-content;

  &:focus-visible {
    --border-color: ${COLORS['Text.Grey.Inactive']};
  }

  &[data-step='initial'] {
    border-radius: 25px;
  }

  &[data-step='optionSelection'],
  &[data-step='feedbackInput'] {
    width: min-content;
  }
`;

const ThanksForYourFeebackWrapper = styled.div`
  ${widgetPosition}
`;
